/**
 * Derived task tables for the S-curve, built from the raw `tasks` API table.
 *
 * The API returns the nested parts (TimeRanges, Subtasks) as JSON strings in
 * VARCHAR columns, so we parse them with from_json() and UNNEST into flat rows.
 *
 *   schedules (raw) ─►  default_schedule  the single schedule with IsDefault = true
 *
 *   tasks (raw)  ─►  leaf_tasks     one row per task on the DEFAULT SCHEDULE that is nobody's parent,
 *                                   with its DEFAULT time range (start, end, duration, progress)
 *                ─►  leaf_subtasks  one row per leaf task × sub-task (ItemId → issues.Id)
 *
 *   sub_task_progress_history (raw)  ─►  subtask_item_progress_history
 *                                        one row per item × date, typed (DATE / DOUBLE) and
 *                                        joined to leaf_subtasks so each row knows its
 *                                        subTaskId / taskId / weighting / leadByTeamId
 *
 *   S-curve — mirrors the Power BI DAX model:
 *     leaf_tasks                     ─►  planned_deltas  "Planned Deltas": each task's durationDays
 *                                                        spread evenly over every day from start to end
 *     subtask_item_progress_history  ─►  actual_deltas   "Actual Deltas": per sub-task, each progress
 *                                                        change × weighting × task duration / 100
 *     (calendar "Date_Table Leaf" is generated inside sCurveSql() from the selected scope's
 *      earliest planned start → latest planned end / last recorded actual)
 *     leaf_tasks + leaf_subtasks     ─►  s_curve_units   one row per (task, sub-task) with the sub-task's
 *                                                        share of the task's man-days and its lead team —
 *                                                        the grain that team / task filters operate on
 *     all of the above               ─►  s_curve         per date: Planned / Actual Man Days (running
 *                                                        sums), Total Man Days, Target % and Actual %
 *                                                        (unfiltered; sCurveSql() builds the filtered form)
 */

// Type specs for from_json(); only the fields we use are listed, the rest are ignored.
const TIME_RANGE_SPEC = `[{
  "Progress": "DOUBLE",
  "IsDefault": "BOOLEAN",
  "TimeRangeTemplateId": "VARCHAR",
  "TimeRangeTemplateTitle": "VARCHAR",
  "StartDate": "VARCHAR",
  "EndDate": "VARCHAR",
  "Duration": {"Value": "DOUBLE", "Unit": "VARCHAR", "Weighting": "DOUBLE"},
  "CalendarId": "VARCHAR",
  "CalendarVersionReference": "VARCHAR"
}]`

const SUBTASK_SPEC = `[{
  "id": "VARCHAR",
  "Title": "VARCHAR",
  "ItemId": "VARCHAR",
  "ItemType": "VARCHAR",
  "ItemTemplateCode": "VARCHAR",
  "Weighting": "DOUBLE",
  "Progress": "DOUBLE",
  "LeadByTeamId": "VARCHAR"
}]`

const sqlList = ids => ids.map(id => `'${String(id).replace(/'/g, "''")}'`).join(', ')

/**
 * Permission scope: SQL that keeps only rows led by one of the current user's teams.
 *   null  → no lock (TRUE)
 *   []    → the user is in no team, so nothing is in scope (FALSE)
 */
export const teamLockSql = (lockedTeamIds, column = 'u.leadByTeamId') =>
  lockedTeamIds === null || lockedTeamIds === undefined ? 'TRUE'
    : lockedTeamIds.length ? `${column} IN (${sqlList(lockedTeamIds)})` : 'FALSE'

// WHERE clauses for s_curve_units: the permission lock first, then the user's selections.
const unitsWhere = ({ teamIds = [], taskIds = [], lockedTeamIds = null } = {}) => {
  const where = [teamLockSql(lockedTeamIds)]
  if (teamIds.length) where.push(`u.leadByTeamId IN (${sqlList(teamIds)})`)
  // A selected task includes its whole subtree (task_ancestors maps leaf → every ancestor).
  if (taskIds.length) where.push(`u.taskId IN (SELECT taskId FROM task_ancestors WHERE ancestorId IN (${sqlList(taskIds)}))`)
  return where
}

// Cross-filters set by clicking a visual (taskSCurveFilters.js): a status, or a drawing set /
// drawing. `scope.ignore` lists the dimensions a query must not apply, so the visual that owns
// a selection isn't filtered by it.
const activeCrossFilters = (scope = {}) => {
  const ignore = scope.ignore ?? []
  const statuses = ignore.includes('status') ? [] : (scope.statuses ?? [])
  const drawingSetIds = ignore.includes('drawing') ? [] : (scope.drawingSetIds ?? [])
  const drawingIds = ignore.includes('drawing') ? [] : (scope.drawingIds ?? [])
  const workload = ignore.includes('workload') ? null : (scope.workload ?? null)
  const recentTaskIds = ignore.includes('recent') ? [] : (scope.recentTaskIds ?? [])
  const waitingTaskIds = ignore.includes('waiting') ? [] : (scope.waitingTaskIds ?? [])
  const detailsTaskIds = ignore.includes('details') ? [] : (scope.detailsTaskIds ?? [])
  const any = statuses.length + drawingSetIds.length + drawingIds.length + recentTaskIds.length + waitingTaskIds.length + detailsTaskIds.length > 0 || workload !== null
  return { statuses, drawingSetIds, drawingIds, workload, recentTaskIds, waitingTaskIds, detailsTaskIds, any }
}

/**
 * The `units` CTE every query starts from: s_curve_units narrowed by the permission lock, the
 * team / task selectors and, when set, the cross-filters. With no cross-filter it is the plain
 * WHERE on s_curve_units. With one, the tasks are first classified (same maths and rules as
 * taskStatusSql, on the lock + selector scope) and / or matched to the selected drawings, and
 * `units` keeps only the rows of the tasks that pass.
 *   Status keys: completed · onSchedule · behindSchedule · overdue · upcoming (sync)
 *                completed · inProgress · stalled · notStarted (lite; stalled = no update in 7+ days)
 */
const unitsCtes = (scope = {}) => {
  const base = `SELECT * FROM s_curve_units u WHERE ${unitsWhere(scope).join(' AND ')}`
  const cf = activeCrossFilters(scope)
  if (!cf.any) return `units AS (
    ${base}
  )`

  const taskWhere = ['TRUE']
  if (cf.statuses.length) taskWhere.push(`cs.status IN (${sqlList(cf.statuses)})`)
  if (cf.drawingSetIds.length || cf.drawingIds.length) {
    const drawingWhere = []
    if (cf.drawingSetIds.length) drawingWhere.push(`d.drawingSetId IN (${sqlList(cf.drawingSetIds)})`)
    if (cf.drawingIds.length) drawingWhere.push(`d.id IN (${sqlList(cf.drawingIds)})`)
    // a task is on a drawing through leaf_task_locations (its own locations, else the nearest ancestor's)
    taskWhere.push(`cs.taskId IN (
      SELECT ltl.taskId
      FROM leaf_task_locations ltl
      JOIN drawings d ON d.id = ltl.drawingId
      WHERE ${drawingWhere.join(' OR ')})`)
  }
  // A Workload bar: same binning as workloadSql (past = completed in that period, by completion
  // date; next = open and due in that period, the first period also holding anything already late)
  if (cf.workload) {
    const step = Math.max(1, Number(cf.workload.step) || 7)
    const bin = Math.max(0, Number(cf.workload.bin) || 0)
    taskWhere.push(cf.workload.side === 'past'
      ? `(cs.status = 'completed' AND cs.lastDate IS NOT NULL
          AND date_diff('day', cs.lastDate, CURRENT_DATE) >= 0
          AND CAST(FLOOR(date_diff('day', cs.lastDate, CURRENT_DATE) / ${step}) AS INTEGER) = ${bin})`
      : `(cs.status <> 'completed' AND cs.plannedEndDate IS NOT NULL
          AND GREATEST(0, CAST(CEIL(date_diff('day', CURRENT_DATE, cs.plannedEndDate) / ${step}.0) AS INTEGER) - 1) = ${bin})`)
  }
  // A row clicked in Recent activity / Others waiting on us / Task details: that task
  if (cf.recentTaskIds.length) taskWhere.push(`cs.taskId IN (${sqlList(cf.recentTaskIds)})`)
  if (cf.waitingTaskIds.length) taskWhere.push(`cs.taskId IN (${sqlList(cf.waitingTaskIds)})`)
  if (cf.detailsTaskIds.length) taskWhere.push(`cs.taskId IN (${sqlList(cf.detailsTaskIds)})`)

  return `units_base AS (
    ${base}
  ),
  cf_planned AS (
    SELECT taskId, SUM(deltaManDays) AS plannedToDate
    FROM planned_deltas WHERE date <= CURRENT_DATE GROUP BY taskId
  ),
  cf_actual AS (
    SELECT subTaskId, SUM(deltaManDays) AS actualToDate, MAX(date) AS lastDate
    FROM actual_deltas WHERE date <= CURRENT_DATE GROUP BY subTaskId
  ),
  cf_metrics AS (
    SELECT u.taskId, lt.durationDays, CAST(lt.plannedEndDate AS DATE) AS plannedEndDate,
           SUM(u.manDays)                    AS capacity,
           MAX(COALESCE(p.plannedToDate, 0)) AS plannedToDate,
           SUM(COALESCE(a.actualToDate, 0))  AS actualToDate,
           MAX(a.lastDate)                   AS lastDate
    FROM units_base u
    JOIN leaf_tasks lt ON lt.taskId = u.taskId
    LEFT JOIN cf_planned p ON p.taskId = u.taskId
    LEFT JOIN cf_actual  a ON a.subTaskId = u.subTaskId
    GROUP BY u.taskId, lt.durationDays, lt.plannedEndDate
  ),
  cf_status AS (
    SELECT c.taskId, c.lastDate, c.plannedEndDate,
      CASE
        WHEN c.actualPct >= 99.99                                          THEN 'completed'
        WHEN m.mode = 'lite' AND c.actualPct > 0 AND c.daysSinceUpdate >= 7 THEN 'stalled'
        WHEN m.mode = 'lite' AND c.actualPct > 0                           THEN 'inProgress'
        WHEN m.mode = 'lite'                                               THEN 'notStarted'
        WHEN c.actualPct > 0 AND c.actualPct >= COALESCE(c.plannedPct, 0)  THEN 'onSchedule'
        WHEN c.actualPct > 0                                               THEN 'behindSchedule'
        WHEN COALESCE(c.plannedPct, 0) > 0                                 THEN 'overdue'
        ELSE 'upcoming'
      END AS status
    FROM (
      SELECT taskId, lastDate, plannedEndDate,
             CASE WHEN capacity > 0     THEN actualToDate  / capacity     * 100 ELSE 0 END AS actualPct,
             CASE WHEN durationDays > 0 THEN plannedToDate / durationDays * 100 END        AS plannedPct,
             date_diff('day', lastDate, CURRENT_DATE)                                      AS daysSinceUpdate
      FROM cf_metrics
    ) c
    CROSS JOIN schedule_mode m
  ),
  units AS (
    SELECT * FROM units_base u
    WHERE u.taskId IN (SELECT cs.taskId FROM cf_status cs WHERE ${taskWhere.join(' AND ')})
  )`
}

/**
 * The S-curve query, optionally filtered by lead team and/or leaf task.
 *
 *   Planned Man Days  = running SUM over (task delta × sub-task share)  for the selected units
 *   Actual Man Days   = running SUM over actual_deltas                   for the selected sub-tasks
 *   Total Man Days    = SUM(manDays) over the selected units
 *   Target / Actual % = ÷ Total; Actual is NULL after today (DAX: BLANK when PeriodStart > TODAY())
 *
 * With no filters this is exactly the DAX model; with filters the denominator is the selected
 * scope's man-days so the curve still reaches 100%. planned_deltas / actual_deltas / s_curve_units
 * are all a few thousand rows, so this runs in milliseconds and needs no rebuild per filter.
 */
export function sCurveSql(scope = {}) {
  return `
  WITH ${unitsCtes(scope)},
  total AS (
    SELECT SUM(manDays) AS totalManDays FROM units
  ),
  -- Calendar bounded to the selected scope: the day before the earliest planned start (so
  -- both curves visibly begin at 0%) → latest planned end, extended if actual progress was
  -- recorded after the planned end (late work must still land inside the running sum).
  -- PM lite has no planned dates, so the calendar runs from the day before the first
  -- recorded progress up to today.
  bounds AS (
    SELECT
      CAST(COALESCE(p.minPlanned, a.minActual) - INTERVAL 1 DAY AS DATE) AS minDate,
      GREATEST(p.maxPlanned, a.maxActual, CASE WHEN m.mode = 'lite' THEN CURRENT_DATE END) AS maxDate
    FROM (
      SELECT MIN(CAST(lt.plannedStartDate AS DATE)) AS minPlanned,
             MAX(CAST(lt.plannedEndDate AS DATE))   AS maxPlanned
      FROM leaf_tasks lt
      WHERE lt.taskId IN (SELECT taskId FROM units)
    ) p
    CROSS JOIN (
      SELECT MIN(ad.date) AS minActual, MAX(ad.date) AS maxActual
      FROM actual_deltas ad JOIN units u ON u.subTaskId = ad.subTaskId
    ) a
    CROSS JOIN schedule_mode m
  ),
  dates AS (
    SELECT CAST(g.d AS DATE) AS date
    FROM bounds b
    CROSS JOIN generate_series(b.minDate, b.maxDate, INTERVAL 1 DAY) AS g(d)
    WHERE b.minDate IS NOT NULL
  ),
  planned_daily AS (
    SELECT pd.date, SUM(pd.deltaManDays * u.weightingShare) AS deltaManDays
    FROM planned_deltas pd
    JOIN units u ON u.taskId = pd.taskId
    GROUP BY pd.date
  ),
  actual_daily AS (
    SELECT ad.date, SUM(ad.deltaManDays) AS deltaManDays
    FROM actual_deltas ad
    JOIN units u ON u.subTaskId = ad.subTaskId
    GROUP BY ad.date
  ),
  cum AS (
    SELECT
      d.date,
      COALESCE(p.deltaManDays, 0) AS plannedDeltaManDays,
      COALESCE(a.deltaManDays, 0) AS actualDeltaManDays,
      SUM(COALESCE(p.deltaManDays, 0)) OVER (ORDER BY d.date) AS plannedManDays,
      SUM(COALESCE(a.deltaManDays, 0)) OVER (ORDER BY d.date) AS actualManDays,
      total.totalManDays
    FROM dates d
    LEFT JOIN planned_daily p ON p.date = d.date
    LEFT JOIN actual_daily  a ON a.date = d.date
    CROSS JOIN total
  )
  SELECT
    date,
    plannedDeltaManDays,
    actualDeltaManDays,
    plannedManDays,
    actualManDays,
    totalManDays,
    -- No planned curve in PM lite (it would sit at 0%)
    CASE WHEN m.mode = 'sync' AND totalManDays > 0
         THEN plannedManDays / totalManDays * 100 END                        AS targetProgressPct,
    CASE WHEN totalManDays > 0 AND date <= CURRENT_DATE
         THEN actualManDays / totalManDays * 100 END                        AS actualProgressPct,
    m.mode AS scheduleMode
  FROM cum
  CROSS JOIN schedule_mode m
  ORDER BY date
  `
}

/**
 * Schedule summary for the cards beside the S-curve, for the same team / task scope.
 * One row:
 *   plannedStartDate / plannedEndDate  earliest start / latest end of the leaf tasks in scope
 *   daysLeft                           days from today to plannedEndDate (0 once it has passed)
 *   daysBehind                         DAX "Days Behind Schedule": find the latest date (up to
 *                                      today) whose planned % was still <= today's actual %,
 *                                      or the first curve date if none; days from there to today
 *                                      (min 0). NULL when no actual progress is recorded in scope.
 */
export function scheduleSummarySql(scope = {}) {
  return `
  WITH ${unitsCtes(scope)},
  curve AS (${sCurveSql(scope)}),
  -- DAX: Curve = calendar dates <= TODAY() with their planned %
  past AS (
    SELECT date, plannedManDays, actualManDays, totalManDays FROM curve WHERE date <= CURRENT_DATE
  ),
  -- DAX: ActualToday = actual man-days up to today ÷ total (read off today's curve row)
  now_row AS (
    SELECT * FROM past ORDER BY date DESC LIMIT 1
  ),
  -- DAX: ActualToday is BLANK when no actual deltas fall on or before today
  actual_recorded AS (
    SELECT COUNT(*) AS n
    FROM actual_deltas ad JOIN units u ON u.subTaskId = ad.subTaskId
    WHERE ad.date <= CURRENT_DATE
  ),
  -- DAX: PlanDate = latest date with planned % <= ActualToday; Anchor falls back to the first date
  anchor AS (
    SELECT COALESCE(
      (SELECT MAX(p.date) FROM past p, now_row n
       WHERE p.plannedManDays / n.totalManDays <= n.actualManDays / n.totalManDays),
      (SELECT MIN(date) FROM past)
    ) AS date
  ),
  span AS (
    SELECT MIN(CAST(plannedStartDate AS DATE)) AS startDate,
           MAX(CAST(plannedEndDate AS DATE))   AS endDate
    FROM leaf_tasks
    WHERE taskId IN (SELECT taskId FROM units)
  )
  SELECT
    CAST(s.startDate AS VARCHAR) AS plannedStartDate,
    CAST(s.endDate AS VARCHAR)   AS plannedEndDate,
    CASE WHEN s.endDate IS NOT NULL
         THEN GREATEST(0, date_diff('day', CURRENT_DATE, s.endDate))
    END AS daysLeft,
    -- Needs a planned curve to compare against, so NULL in PM lite
    CASE WHEN m.mode = 'sync'
          AND (SELECT totalManDays FROM now_row) > 0 AND (SELECT n FROM actual_recorded) > 0
         THEN GREATEST(0, date_diff('day', (SELECT date FROM anchor), CURRENT_DATE))
    END AS daysBehind,
    m.mode AS scheduleMode
  FROM span s
  CROSS JOIN schedule_mode m
  `
}

/**
 * Task status counts for the card row at the top of the Tasks tab, same team / task scope.
 * One row. Each leaf task in scope lands in exactly one status, evaluated as of today:
 *
 *   completed       actual progress 100%
 *   -- sync only (needs planned dates) --
 *   onSchedule      actual % > 0 and actual % >= the task's own planned % today ("in progress")
 *   behindSchedule  actual % > 0 and actual % <  the task's own planned % today
 *   overdue         actual % = 0 while planned % > 0 (the plan expected progress by now)
 *   upcoming        actual % = 0 and nothing planned yet (planned start after today)
 *   -- lite only --
 *   notStarted      no progress yet
 *   inProgress      has progress
 *
 * Plus: open = total - completed, delayed = overdue + behindSchedule, overdueMaxDays = the longest
 * wait since planned start among overdue tasks. With a team filter a task's actual % is measured
 * on that team's sub-tasks only (same grain as the curve).
 */
export function taskStatusSql(scope = {}) {
  return `
  WITH ${unitsCtes(scope)},
  planned_by_task AS (
    SELECT taskId, SUM(deltaManDays) AS plannedToDate
    FROM planned_deltas WHERE date <= CURRENT_DATE GROUP BY taskId
  ),
  actual_by_subtask AS (
    SELECT subTaskId, SUM(deltaManDays) AS actualToDate, MAX(date) AS lastDate
    FROM actual_deltas WHERE date <= CURRENT_DATE GROUP BY subTaskId
  ),
  task_metrics AS (
    SELECT
      u.taskId,
      CAST(lt.plannedStartDate AS DATE)         AS startDate,
      lt.durationDays,
      SUM(u.manDays)                            AS capacity,
      MAX(COALESCE(p.plannedToDate, 0))         AS plannedToDate,
      SUM(COALESCE(a.actualToDate, 0))          AS actualToDate,
      MAX(a.lastDate)                           AS lastDate
    FROM units u
    JOIN leaf_tasks lt ON lt.taskId = u.taskId
    LEFT JOIN planned_by_task   p ON p.taskId    = u.taskId
    LEFT JOIN actual_by_subtask a ON a.subTaskId = u.subTaskId
    GROUP BY u.taskId, lt.plannedStartDate, lt.durationDays
  ),
  classified AS (
    SELECT
      taskId,
      startDate,
      -- days since the task's latest progress update (NULL when it has none)
      date_diff('day', lastDate, CURRENT_DATE)  AS daysSinceUpdate,
      CASE WHEN capacity > 0     THEN actualToDate  / capacity     * 100 ELSE 0 END AS actualPct,
      CASE WHEN durationDays > 0 THEN plannedToDate / durationDays * 100 END        AS plannedPct
    FROM task_metrics
  ),
  status AS (
    SELECT
      c.*,
      CASE
        -- shares are fractions, so a fully complete task can sum to 99.999…
        WHEN c.actualPct >= 99.99                          THEN 'completed'
        WHEN m.mode = 'lite' AND c.actualPct > 0           THEN 'inProgress'
        WHEN m.mode = 'lite'                               THEN 'notStarted'
        WHEN c.actualPct > 0 AND c.actualPct >= COALESCE(c.plannedPct, 0) THEN 'onSchedule'
        WHEN c.actualPct > 0                               THEN 'behindSchedule'
        WHEN COALESCE(c.plannedPct, 0) > 0                 THEN 'overdue'
        ELSE 'upcoming'
      END AS status
    FROM classified c
    CROSS JOIN schedule_mode m
  )
  SELECT
    COUNT(*)                                                        AS total,
    COUNT(*) FILTER (WHERE status <> 'completed')                   AS open,
    COUNT(*) FILTER (WHERE status = 'upcoming')                     AS upcoming,
    COUNT(*) FILTER (WHERE status = 'onSchedule')                   AS onSchedule,
    COUNT(*) FILTER (WHERE status = 'behindSchedule')               AS behindSchedule,
    COUNT(*) FILTER (WHERE status = 'overdue')                      AS overdue,
    MAX(date_diff('day', startDate, CURRENT_DATE))
      FILTER (WHERE status = 'overdue')                             AS overdueMaxDays,
    COUNT(*) FILTER (WHERE status IN ('overdue', 'behindSchedule')) AS delayed,
    COUNT(*) FILTER (WHERE status = 'completed')                    AS completed,
    COUNT(*) FILTER (WHERE status = 'notStarted')                   AS notStarted,
    COUNT(*) FILTER (WHERE status = 'inProgress')                   AS inProgress,
    -- PM lite extras: in progress but not updated for a week, and completions per week
    COUNT(*) FILTER (WHERE status = 'inProgress' AND daysSinceUpdate >= 7) AS stalled,
    COUNT(*) FILTER (WHERE status = 'completed' AND daysSinceUpdate BETWEEN 0 AND 6)  AS completedThisWeek,
    COUNT(*) FILTER (WHERE status = 'completed' AND daysSinceUpdate BETWEEN 7 AND 13) AS completedLastWeek,
    (SELECT mode FROM schedule_mode)                                AS scheduleMode
  FROM status
  `
}

// Exported so the statements can be run/inspected outside createTaskTables (e.g. in the DuckDB query dialog).
export const TASK_TABLE_SQL = [
  // The project's default schedule. Everything downstream is scoped to it: tasks from other
  // schedules (alternative plans, archived baselines) never reach leaf_tasks.
  `
  CREATE OR REPLACE TABLE default_schedule AS
  SELECT * FROM schedules WHERE IsDefault;
  `,
  // Two kinds of schedule reach the dashboard. 'sync': synced from a scheduling tool, every
  // task has a planned start / end. 'lite': built in RDrive with no dates (or durations) on any
  // level. The API has no type flag, so the mode is read from the data: any default time range
  // with both dates on the default schedule → sync, otherwise lite. In lite mode every leaf
  // task counts as 1 day of work, so actual progress can still be measured and charted over
  // time; the planned curve and everything derived from it have no meaning and are left NULL.
  `
  CREATE OR REPLACE TABLE schedule_mode AS
  SELECT CASE WHEN EXISTS (
    SELECT 1
    FROM tasks t
    JOIN default_schedule ds ON ds.Id = t.ScheduleId
    CROSS JOIN UNNEST(from_json(t.TimeRanges, '${TIME_RANGE_SPEC}')) AS u(r)
    WHERE u.r.IsDefault AND u.r.StartDate IS NOT NULL AND u.r.EndDate IS NOT NULL
  ) THEN 'sync' ELSE 'lite' END AS mode;
  `,
  // Leaf = a task on the default schedule whose Id never appears as another task's ParentTaskId.
  // Only the default time range (IsDefault = true) is kept — one row per leaf task.
  `
  CREATE OR REPLACE TABLE leaf_tasks AS
  SELECT
    t.Id           AS taskId,
    t.Title        AS taskTitle,
    t.ScheduleId   AS scheduleId,
    t.ParentTaskId AS parentTaskId,
    t.Sort         AS sort,
    CAST(r.StartDate AS TIMESTAMP) AS plannedStartDate,
    CAST(r.EndDate   AS TIMESTAMP) AS plannedEndDate,
    -- Inclusive day count: a task that starts and ends on the same day is 1 day of work,
    -- not 0. This matches the span used to spread the planned deltas.
    -- PM lite schedules have no dates at all, so every task is taken as 1 day of work.
    -- TODO: weekends/holidays are not excluded yet - see the calendars table.
    CASE WHEN m.mode = 'lite' THEN 1
         ELSE date_diff('day', CAST(r.StartDate AS DATE), CAST(r.EndDate AS DATE)) + 1
    END AS durationDays,
    r.Duration.Value     AS durationValue,
    r.Duration.Unit      AS durationUnit,
    r.Duration.Weighting AS durationWeighting,
    -- The calendar this time range's dates were computed against. Versioned, so a join to the
    -- calendars table may need both columns to get the definition that was in force.
    r.CalendarId               AS calendarId,
    r.CalendarVersionReference AS calendarVersionReference,
    r.Progress           AS progress,
    -- Needed with scheduleId to build the task-viewer URL
    r.TimeRangeTemplateId AS timeRangeTemplateId
  FROM tasks t
  JOIN default_schedule ds ON ds.Id = t.ScheduleId
  CROSS JOIN schedule_mode m
  LEFT JOIN (
    SELECT t2.Id AS taskId, u.r
    FROM tasks t2
    CROSS JOIN UNNEST(from_json(t2.TimeRanges, '${TIME_RANGE_SPEC}')) AS u(r)
    WHERE u.r.IsDefault
  ) d ON d.taskId = t.Id
  WHERE t.Id NOT IN (
    SELECT p.ParentTaskId FROM tasks p
    JOIN default_schedule ds2 ON ds2.Id = p.ScheduleId
    WHERE p.ParentTaskId IS NOT NULL
  );
  `,
  `
  CREATE OR REPLACE TABLE leaf_subtasks AS
  SELECT
    lt.taskId,
    s.id               AS subTaskId,
    s.Title            AS subTaskTitle,
    s.ItemId           AS itemId,
    s.ItemType         AS itemType,
    s.ItemTemplateCode AS itemTemplateCode,
    s.Weighting        AS weighting,
    -- Share of the task's total weighting (sums to 1 per task). This is what the
    -- progress roll-up uses; the raw Weighting (usually 1 per sub-task) would credit
    -- each sub-task with the whole task duration.
    s.Weighting / SUM(s.Weighting) OVER (PARTITION BY lt.taskId) AS weightingShare,
    s.Progress         AS progress,
    s.LeadByTeamId     AS leadByTeamId
  FROM leaf_tasks lt
  JOIN tasks t ON t.Id = lt.taskId
  CROSS JOIN UNNEST(from_json(t.Subtasks, '${SUBTASK_SPEC}')) AS u(s)
  WHERE t.Subtasks IS NOT NULL;
  `,
  // Actual progress over time. The raw API table has Date as VARCHAR ('2026-09-07')
  // and Percentage as DECIMAL(38,18); cast both so downstream SQL can do date maths
  // and plain arithmetic. INNER JOIN keeps only items that belong to a leaf sub-task.
  `
  CREATE OR REPLACE TABLE subtask_item_progress_history AS
  SELECT
    h.ItemId                        AS itemId,
    CAST(h.Date AS DATE)            AS date,
    CAST(h.Percentage AS DOUBLE)    AS percentage,
    ls.subTaskId,
    ls.taskId,
    ls.weighting                    AS subtaskWeighting,
    ls.weightingShare               AS subtaskWeightingShare,
    ls.leadByTeamId
  FROM sub_task_progress_history h
  JOIN leaf_subtasks ls ON ls.itemId = h.ItemId
  ORDER BY h.ItemId, CAST(h.Date AS DATE);
  `,

  // ── Planned S-curve ─────────────────────────────────────────────────────────
  // DAX "Planned Deltas": FILTER(valid tasks) → GENERATE(CALENDAR(s, e)) → Delta = DIVIDE(dur, span)
  // span = DATEDIFF(s, e, DAY) + 1 (inclusive day count), so each task's deltas sum to its durationDays.
  // TODO: every calendar day gets a share, including weekends - see the calendars table.
  `
  CREATE OR REPLACE TABLE planned_deltas AS
  SELECT
    lt.taskId,
    CAST(g.d AS DATE) AS date,
    lt.durationDays / (date_diff('day', CAST(lt.plannedStartDate AS DATE), CAST(lt.plannedEndDate AS DATE)) + 1) AS deltaManDays
  FROM leaf_tasks lt
  CROSS JOIN generate_series(CAST(lt.plannedStartDate AS DATE), CAST(lt.plannedEndDate AS DATE), INTERVAL 1 DAY) AS g(d)
  WHERE lt.plannedStartDate IS NOT NULL
    AND lt.plannedEndDate IS NOT NULL
    AND lt.durationDays IS NOT NULL
    AND lt.durationDays > 0
    AND lt.plannedEndDate >= lt.plannedStartDate;
  `,
  // ── Actual S-curve ──────────────────────────────────────────────────────────
  // DAX "Actual Deltas": for each history row, @Prev = the sub-task's progress at its previous
  // recorded date (0 if none), @Dur = the leaf task's durationDays, and
  //   Delta Man Days = DIVIDE((progress - @Prev) * subTaskWeighting * @Dur, 100)
  // where subTaskWeighting is the sub-task's SHARE of its task's weighting (sums to 1 per task),
  // so a fully complete task contributes exactly its durationDays.
  // Rows are first collapsed to one per (subTaskId, date) with MAX(progress), which is what the
  // DAX CALCULATE(MAX(progress), date = prevDate) does when a date has several rows.
  `
  CREATE OR REPLACE TABLE actual_deltas AS
  WITH per_day AS (
    SELECT subTaskId, taskId, date,
           MAX(percentage)            AS progress,
           MAX(subtaskWeightingShare) AS subtaskWeightingShare
    FROM subtask_item_progress_history
    WHERE taskId IS NOT NULL AND taskId <> ''
      AND subTaskId IS NOT NULL
      AND date IS NOT NULL
      AND percentage IS NOT NULL
    GROUP BY subTaskId, taskId, date
  ),
  with_prev AS (
    SELECT *,
           LAG(progress, 1, 0) OVER (PARTITION BY subTaskId ORDER BY date) AS prevProgress
    FROM per_day
  )
  SELECT
    w.taskId,
    w.subTaskId,
    w.date,
    w.progress,
    w.prevProgress,
    (w.progress - w.prevProgress) * w.subtaskWeightingShare * lt.durationDays / 100 AS deltaManDays
  FROM with_prev w
  JOIN leaf_tasks lt ON lt.taskId = w.taskId;
  `,
  // The filter grain. Every leaf task appears at least once: once per sub-task (with that
  // sub-task's share of the task's man-days and its lead team), or once with share 1 and no
  // team if the task has no sub-tasks. Shares sum to 1 per task, so with no filter the totals
  // equal SUM(durationDays) exactly as in the DAX.
  `
  CREATE OR REPLACE TABLE s_curve_units AS
  SELECT
    lt.taskId,
    ls.subTaskId,
    ls.leadByTeamId,
    COALESCE(ls.weightingShare, 1) AS weightingShare,
    COALESCE(lt.durationDays, 0) * COALESCE(ls.weightingShare, 1) AS manDays
  FROM leaf_tasks lt
  LEFT JOIN leaf_subtasks ls ON ls.taskId = lt.taskId;
  `,
  // Ancestor closure over the default schedule: (task, every ancestor incl. itself).
  // Lets a task filter on a parent select its whole subtree with a single IN (...).
  `
  CREATE OR REPLACE TABLE task_ancestors AS
  WITH RECURSIVE
  sched_tasks AS (
    SELECT t.Id AS taskId, t.ParentTaskId AS parentTaskId
    FROM tasks t JOIN default_schedule ds ON ds.Id = t.ScheduleId
  ),
  anc AS (
    SELECT taskId, taskId AS ancestorId, 0 AS distance FROM sched_tasks
    UNION ALL
    SELECT a.taskId, s.parentTaskId, a.distance + 1
    FROM anc a JOIN sched_tasks s ON s.taskId = a.ancestorId
    WHERE s.parentTaskId IS NOT NULL
      AND s.parentTaskId IN (SELECT taskId FROM sched_tasks)
  )
  SELECT * FROM anc;
  `,
  // DAX measures evaluated per calendar date (unfiltered). The chart runs the same SQL with
  // filters applied at query time — see sCurveSql() below.
  `CREATE OR REPLACE TABLE s_curve AS ${sCurveSql()}`,
]

/**
 * @param conn      DuckDB connection wrapper (needs queryRaw)
 * @param options   { siteUrl, projectCode } — the RDrive site (e.g. https://baj.rdrive.io) and
 *                  project code, used to build plan-viewer links in leaf_task_locations
 */
export async function createTaskTables(conn, options = {}) {
  const tables = (await conn.queryRaw(`SELECT table_name FROM information_schema.tables`))
    .toArray().map(r => r.toJSON().table_name)
  const missing = ['tasks', 'schedules', 'sub_task_progress_history'].filter(t => !tables.includes(t))
  if (missing.length) {
    console.warn(`[taskTables] raw table(s) not loaded: ${missing.join(', ')}; skipping derived task tables`)
    return false
  }

  // No default schedule → nothing for the Tasks tab to show, so none of the derived tables are
  // built and the caller hides the tab (the return value drives that).
  const defaultSchedules = Number((await conn.queryRaw(`SELECT COUNT(*) AS n FROM schedules WHERE IsDefault`))
    .toArray()[0]?.toJSON().n ?? 0)
  if (defaultSchedules === 0) {
    console.warn('[taskTables] the project has no default schedule; skipping derived task tables and hiding the Tasks tab')
    return false
  }

  try {
    const t0 = performance.now()
    for (const sql of TASK_TABLE_SQL) {
      const t = performance.now()
      await conn.queryRaw(sql) // throws on error, unlike loadData
      const name = sql.match(/TABLE\s+(\w+)/)?.[1] ?? '?'
      console.log(`[taskTables] ${name} built in ${Math.round(performance.now() - t)}ms`)
    }
    console.log(`[taskTables] all derived tables built in ${Math.round(performance.now() - t0)}ms`)

    const schedules = (await conn.queryRaw(`SELECT * FROM default_schedule`)).toArray().map(r => r.toJSON())
    if (schedules.length !== 1) {
      console.warn(`[taskTables] expected exactly 1 default schedule, found ${schedules.length}:`, schedules)
    } else {
      const ds = schedules[0]
      console.log(`[taskTables] default schedule: ${ds.Title ?? ds.Name ?? ''} (${ds.Id})`)
    }

    const taskColumns = (await conn.queryRaw(`SELECT column_name FROM information_schema.columns WHERE table_name = 'tasks' ORDER BY ordinal_position`)).toArray().map(r => r.toJSON().column_name)
    console.log('[taskTables] tasks columns:', taskColumns.join(', '))

    await createPrerequisitesTable(conn, taskColumns)
    await createTaskLocationsTable(conn, taskColumns, options)

    const counts = (await conn.queryRaw(`
      SELECT
        (SELECT mode FROM schedule_mode)                                AS mode,
        (SELECT COUNT(*) FROM schedules)                                AS schedules,
        (SELECT COUNT(*) FROM tasks)                                    AS tasks,
        (SELECT COUNT(*) FROM tasks t JOIN default_schedule ds ON ds.Id = t.ScheduleId) AS tasks_on_default,
        (SELECT COUNT(*) FROM leaf_tasks)                               AS leaf_tasks,
        (SELECT COUNT(*) FROM leaf_tasks WHERE plannedStartDate IS NULL)       AS leaf_tasks_without_default_range,
        (SELECT COUNT(*) FROM leaf_subtasks)                            AS subtasks,
        (SELECT COUNT(*) FROM sub_task_progress_history)                AS history_raw,
        (SELECT COUNT(*) FROM subtask_item_progress_history)            AS history_matched,
        (SELECT COUNT(*) FROM s_curve)                                  AS curve_days,
        (SELECT ROUND(MAX(targetProgressPct), 1) FROM s_curve)          AS planned_max_pct,
        (SELECT ROUND(MAX(actualProgressPct), 1) FROM s_curve)          AS actual_max_pct
    `)).toArray()[0].toJSON()
    console.log(`[taskTables] OK mode=${counts.mode} schedules=${counts.schedules} tasks=${counts.tasks} (on default schedule: ${counts.tasks_on_default}) leaf_tasks=${counts.leaf_tasks} (no default range: ${counts.leaf_tasks_without_default_range}) subtasks=${counts.subtasks} history=${counts.history_matched}/${counts.history_raw} rows matched to a leaf sub-task`)
    console.log(`[taskTables] s_curve: ${counts.curve_days} days, planned reaches ${counts.planned_max_pct}%, actual reaches ${counts.actual_max_pct}% (to today)`)
    return true
  } catch (error) {
    console.error('[taskTables] failed to build derived task tables:', error.message)
    return false
  }
}

// ── Per-task metrics for the Tasks tab cards ───────────────────────────────────
// CTEs shared by workloadSql / recentActivitySql. `scoped_tasks` has one row per leaf task in
// the team / task scope with its planned dates, actual % and planned % as of today, the date of
// its latest recorded progress, and whether it is complete. Same grain and maths as taskStatusSql.
const scopedTaskCtes = (scope = {}) => {
  return `
  ${unitsCtes(scope)},
  planned_by_task AS (
    SELECT taskId, SUM(deltaManDays) AS plannedToDate
    FROM planned_deltas WHERE date <= CURRENT_DATE GROUP BY taskId
  ),
  actual_by_subtask AS (
    SELECT subTaskId, SUM(deltaManDays) AS actualToDate, MAX(date) AS lastDate
    FROM actual_deltas WHERE date <= CURRENT_DATE GROUP BY subTaskId
  ),
  task_metrics AS (
    SELECT
      u.taskId,
      SUM(u.manDays)                    AS capacity,
      MAX(COALESCE(p.plannedToDate, 0)) AS plannedToDate,
      SUM(COALESCE(a.actualToDate, 0))  AS actualToDate,
      MAX(a.lastDate)                   AS lastDate
    FROM units u
    LEFT JOIN planned_by_task   p ON p.taskId    = u.taskId
    LEFT JOIN actual_by_subtask a ON a.subTaskId = u.subTaskId
    GROUP BY u.taskId
  ),
  scoped_tasks AS (
    SELECT
      tm.taskId,
      lt.taskTitle,
      lt.scheduleId,
      lt.timeRangeTemplateId,
      lt.durationDays,
      CAST(lt.plannedStartDate AS DATE) AS plannedStartDate,
      CAST(lt.plannedEndDate AS DATE)   AS plannedEndDate,
      tm.lastDate,
      CASE WHEN tm.capacity > 0 THEN tm.actualToDate / tm.capacity * 100 ELSE 0 END AS actualPct,
      CASE WHEN lt.durationDays > 0 THEN LEAST(100, tm.plannedToDate / lt.durationDays * 100) END AS plannedPct,
      -- shares are fractions, so a fully complete task can sum to 99.999…
      (CASE WHEN tm.capacity > 0 THEN tm.actualToDate / tm.capacity * 100 ELSE 0 END) >= 99.99 AS completed
    FROM task_metrics tm
    JOIN leaf_tasks lt ON lt.taskId = tm.taskId
  )`
}

/**
 * Workload card: tasks completed per period (look back) versus open tasks due per period
 * (look ahead), centred on today. `step` is the period in days (1 = daily bins for the
 * one-week view, 7 = weekly) and `bins` the number of periods on each side.
 * Rows: side ('past' | 'next'), bin (0 = nearest to today), n.
 *   past bin i = tasks whose progress reached 100% in (today - (i+1)·step, today - i·step];
 *                the completion date is the task's latest recorded progress date
 *   next bin i = open tasks whose planned end falls in (today + i·step, today + (i+1)·step];
 *                bin 0 also holds every open task already past its planned end
 */
export function workloadSql(scope = {}) {
  const { step = 7, bins = 2 } = scope
  const span = Number(step) * Number(bins)
  return `
  WITH ${scopedTaskCtes(scope)}
  SELECT 'past' AS side,
         CAST(FLOOR(date_diff('day', lastDate, CURRENT_DATE) / ${Number(step)}) AS INTEGER) AS bin,
         COUNT(*) AS n
  FROM scoped_tasks
  WHERE completed AND lastDate IS NOT NULL
    AND date_diff('day', lastDate, CURRENT_DATE) BETWEEN 0 AND ${span - 1}
  GROUP BY 2
  UNION ALL
  SELECT 'next' AS side,
         GREATEST(0, CAST(CEIL(date_diff('day', CURRENT_DATE, plannedEndDate) / ${Number(step)}.0) AS INTEGER) - 1) AS bin,
         COUNT(*) AS n
  FROM scoped_tasks
  WHERE NOT completed AND plannedEndDate IS NOT NULL
    AND date_diff('day', CURRENT_DATE, plannedEndDate) <= ${span}
  GROUP BY 2
  `
}

/**
 * Recent activity card: tasks with recorded progress, newest update first. `path` is the
 * task's ancestors on the schedule (top level first), so repeated leaf titles stay distinct.
 */
export function recentActivitySql(scope = {}) {
  const { limit = 50 } = scope
  return `
  WITH ${scopedTaskCtes(scope)},
  path AS (
    SELECT a.taskId, string_agg(t.Title, ' › ' ORDER BY a.distance DESC) AS path
    FROM task_ancestors a
    JOIN tasks t ON t.Id = a.ancestorId
    WHERE a.distance > 0
    GROUP BY a.taskId
  )
  SELECT
    s.taskId,
    s.taskTitle,
    p.path,
    CAST(s.plannedStartDate AS VARCHAR) AS plannedStartDate,
    CAST(s.plannedEndDate AS VARCHAR)   AS plannedEndDate,
    CAST(s.lastDate AS VARCHAR)         AS updatedOn,
    date_diff('day', s.lastDate, CURRENT_DATE) AS updatedDaysAgo,
    s.actualPct,
    s.plannedPct,
    s.completed,
    (SELECT mode FROM schedule_mode)    AS scheduleMode
  FROM scoped_tasks s
  LEFT JOIN path p ON p.taskId = s.taskId
  WHERE s.actualPct > 0 AND s.lastDate IS NOT NULL
  ORDER BY s.lastDate DESC, s.actualPct DESC
  LIMIT ${Number(limit)}
  `
}

// ── Prerequisites (task dependencies) ─────────────────────────────────────────
// The tasks API carries each task's prerequisites (the tasks that must finish before it can
// start). Like TimeRanges / Subtasks it arrives as a JSON string, but the element shape isn't
// pinned down, so every element is parsed defensively: a plain string is taken as the task id,
// an object is read through the usual id keys. One sample element is logged so the shape can
// be checked in the console. The table is always created (empty when the column is missing) so
// the queries that read it never fail.
//
//   task_prerequisites   taskId (the successor, which waits) · prerequisiteTaskId (which it waits on)
const EMPTY_PREREQUISITES = `CREATE OR REPLACE TABLE task_prerequisites (taskId VARCHAR, prerequisiteTaskId VARCHAR, raw VARCHAR)`

export async function createPrerequisitesTable(conn, taskColumns) {
  // The API names it PrerequisiteTaskIds; accept any column starting with "prerequisite"
  const column = taskColumns.find(c => /^prerequisite/i.test(c))
  if (!column) {
    console.warn('[taskTables] tasks has no Prerequisite… column; task_prerequisites will be empty')
    await conn.queryRaw(EMPTY_PREREQUISITES)
    return
  }
  try {
    const type = (await conn.queryRaw(
      `SELECT data_type FROM information_schema.columns WHERE table_name = 'tasks' AND column_name = '${column}'`
    )).toArray()[0]?.toJSON().data_type ?? ''
    // A native LIST column is turned into its JSON text first so every shape goes through one path
    const value = /VARCHAR|JSON/i.test(type) ? `CAST(t.${column} AS VARCHAR)` : `CAST(to_json(t.${column}) AS VARCHAR)`
    await conn.queryRaw(`
      CREATE OR REPLACE TABLE task_prerequisites AS
      WITH src AS (
        SELECT t.Id AS taskId, ${value} AS value
        FROM tasks t
        JOIN default_schedule ds ON ds.Id = t.ScheduleId
        WHERE t.${column} IS NOT NULL AND TRIM(${value}) NOT IN ('', '[]', 'null')
      ),
      -- One task can list many prerequisites. Split the field into one element per link:
      --   JSON array  → each element (a string id or an object)
      --   JSON object → the object itself
      --   anything else (e.g. "id1,id2;id3") → split on comma / semicolon / whitespace
      split AS (
        SELECT taskId, value,
          CASE
            WHEN json_valid(value) AND json_type(value::JSON) = 'ARRAY'  THEN json_extract(value::JSON, '$[*]')
            WHEN json_valid(value) AND json_type(value::JSON) = 'OBJECT' THEN [value::JSON]
            ELSE list_transform(string_split_regex(value, '[,;\\s]+'), x -> to_json(x))
          END AS elements
        FROM src
      )
      SELECT
        s.taskId,
        NULLIF(TRIM(COALESCE(
          json_extract_string(u.e, '$.TaskId'),
          json_extract_string(u.e, '$.taskId'),
          json_extract_string(u.e, '$.PrerequisiteTaskId'),
          json_extract_string(u.e, '$.PredecessorTaskId'),
          json_extract_string(u.e, '$.Id'),
          json_extract_string(u.e, '$.id'),
          CASE WHEN json_type(u.e) = 'VARCHAR' THEN json_extract_string(u.e, '$') END
        )), '') AS prerequisiteTaskId,
        CAST(u.e AS VARCHAR) AS raw
      FROM split s
      CROSS JOIN UNNEST(s.elements) AS u(e)
    `)
    const info = (await conn.queryRaw(`
      SELECT COUNT(*) AS links,
             COUNT(*) FILTER (WHERE prerequisiteTaskId IS NOT NULL
                               AND prerequisiteTaskId IN (SELECT Id FROM tasks)) AS resolved,
             MIN(raw) AS sample
      FROM task_prerequisites
    `)).toArray()[0].toJSON()
    console.log(`[taskTables] task_prerequisites: ${info.links} links, ${info.resolved} resolve to a task (column ${column}, type ${type}); sample element: ${info.sample ?? '–'}`)
  } catch (error) {
    console.error('[taskTables] failed to parse task prerequisites:', error.message)
    await conn.queryRaw(EMPTY_PREREQUISITES)
  }
}

/**
 * "Others waiting on us" card: our unfinished leaf tasks that another team's task lists as a
 * prerequisite. One row per such task with how many tasks wait on it and which teams they belong to.
 *
 *   links         prerequisite links expanded to leaf ↔ leaf through task_ancestors, so a link on
 *                 a parent task applies to every leaf under it (on either end)
 *   task_teams    the lead teams of a task's sub-tasks = the teams that own the task
 *   waiting       with a team filter ("us" = the selected teams): successor tasks owned by any other
 *                 team, or with no team recorded. Without a team filter "us" is the whole schedule,
 *                 so every successor counts and the card reads "tasks others are waiting on".
 */
export function waitingOnUsSql(scope = {}) {
  const { teamIds = [], lockedTeamIds = null } = scope
  // "Us" = the selected teams, else the user's own teams (the permission lock), else everyone
  const usTeams = teamIds.length ? teamIds : (lockedTeamIds ?? [])
  const otherTeam = usTeams.length
    ? `(st.leadByTeamId IS NULL OR st.leadByTeamId NOT IN (${sqlList(usTeams)}))`
    : `TRUE`

  return `
  WITH ${scopedTaskCtes(scope)},
  links AS (
    SELECT DISTINCT pa.taskId AS predecessorTaskId, sa.taskId AS successorTaskId
    FROM task_prerequisites pr
    JOIN task_ancestors pa ON pa.ancestorId = pr.prerequisiteTaskId
    JOIN task_ancestors sa ON sa.ancestorId = pr.taskId
    JOIN leaf_tasks lp ON lp.taskId = pa.taskId
    JOIN leaf_tasks ls ON ls.taskId = sa.taskId
    WHERE pa.taskId <> sa.taskId
  ),
  task_teams AS (
    SELECT DISTINCT taskId, leadByTeamId FROM leaf_subtasks WHERE leadByTeamId IS NOT NULL
  ),
  waiting AS (
    SELECT l.predecessorTaskId, l.successorTaskId, st.leadByTeamId AS waitingTeamId
    FROM links l
    LEFT JOIN task_teams st ON st.taskId = l.successorTaskId
    WHERE ${otherTeam}
  )
  SELECT
    s.taskId,
    s.taskTitle,
    s.actualPct,
    s.plannedPct,
    s.completed,
    CAST(s.plannedEndDate AS VARCHAR) AS plannedEndDate,
    (SELECT array_to_string(list_distinct(list(COALESCE(ot.Name, pt.leadByTeamId))), ', ')
     FROM task_teams pt LEFT JOIN teams ot ON ot.Id = pt.leadByTeamId
     WHERE pt.taskId = s.taskId)                                                   AS ownerTeams,
    COUNT(DISTINCT w.successorTaskId)                                              AS waitingCount,
    array_to_string(list_distinct(list(COALESCE(wt.Name, w.waitingTeamId))), ', ') AS waitingTeams
  FROM scoped_tasks s
  JOIN waiting w ON w.predecessorTaskId = s.taskId
  LEFT JOIN teams wt ON wt.Id = w.waitingTeamId
  -- Unfinished and already due: a task that hasn't reached its planned start (upcoming) isn't
  -- holding anyone up yet, so only started, behind-schedule and overdue tasks appear
  WHERE NOT s.completed
    AND (s.actualPct > 0 OR COALESCE(s.plannedPct, 0) > 0)
  GROUP BY s.taskId, s.taskTitle, s.actualPct, s.plannedPct, s.completed, s.plannedEndDate
  ORDER BY waitingCount DESC, s.plannedEndDate
  `
}

/**
 * Task details grid: every leaf task in scope, one row each, sorted by status (overdue first,
 * completed last) then planned start.
 *   actualStartDate   first day any of the task's sub-tasks recorded progress
 *   completionDate    the task's latest progress date, only once it is complete
 *   overdueDays       days since planned start, for overdue tasks only
 *   cycleDays         actual start → completion (or today while still open); 0 if not started
 *   locationTitles    the task's viewable locations (its own, else the nearest ancestor's), joined
 *   planViews         JSON [{ title, url }] with a plan-viewer link per location
 */
export function taskDetailsSql(scope = {}) {
  return `
  WITH ${scopedTaskCtes(scope)},
  path AS (
    SELECT a.taskId, string_agg(t.Title, ' › ' ORDER BY a.distance DESC) AS path
    FROM task_ancestors a
    JOIN tasks t ON t.Id = a.ancestorId
    WHERE a.distance > 0
    GROUP BY a.taskId
  ),
  first_progress AS (
    SELECT ad.taskId, MIN(ad.date) AS firstDate
    FROM actual_deltas ad
    JOIN units u ON u.subTaskId = ad.subTaskId
    WHERE ad.deltaManDays > 0 AND ad.date <= CURRENT_DATE
    GROUP BY ad.taskId
  ),
  -- viewable locations from leaf_task_locations (own, else nearest ancestor's), titles joined
  task_location AS (
    SELECT taskId,
           array_to_string(list_distinct(list(COALESCE(locationTitle, locationId) ORDER BY locationTitle)), ', ') AS locationTitles,
           -- one plan-viewer link per location, for the grid's Plan viewer icon
           to_json(list(struct_pack(title := COALESCE(locationTitle, locationId), url := planViewUrl) ORDER BY locationTitle)
                   FILTER (WHERE planViewUrl IS NOT NULL)) AS planViews
    FROM leaf_task_locations
    WHERE taskId IN (SELECT taskId FROM scoped_tasks) AND locationId IS NOT NULL
    GROUP BY taskId
  ),
  classified AS (
    SELECT
      s.*,
      p.path,
      tloc.locationTitles,
      tloc.planViews,
      fp.firstDate AS actualStartDate,
      CASE WHEN s.completed THEN s.lastDate END AS completionDate,
      CASE
        WHEN s.completed                                                  THEN 'completed'
        WHEN m.mode = 'lite' AND s.actualPct > 0                          THEN 'inProgress'
        WHEN m.mode = 'lite'                                              THEN 'notStarted'
        WHEN s.actualPct > 0 AND s.actualPct >= COALESCE(s.plannedPct, 0) THEN 'onSchedule'
        WHEN s.actualPct > 0                                              THEN 'behindSchedule'
        WHEN COALESCE(s.plannedPct, 0) > 0                                THEN 'overdue'
        ELSE 'upcoming'
      END AS status,
      m.mode AS scheduleMode
    FROM scoped_tasks s
    LEFT JOIN path p ON p.taskId = s.taskId
    LEFT JOIN task_location tloc ON tloc.taskId = s.taskId
    LEFT JOIN first_progress fp ON fp.taskId = s.taskId
    CROSS JOIN schedule_mode m
  )
  SELECT
    taskId,
    taskTitle,
    scheduleId,
    timeRangeTemplateId,
    path,
    locationTitles,
    CAST(planViews AS VARCHAR) AS planViews,
    status,
    scheduleMode,
    actualPct,
    plannedPct,
    CAST(plannedStartDate AS VARCHAR) AS plannedStartDate,
    CAST(actualStartDate AS VARCHAR)  AS actualStartDate,
    CAST(plannedEndDate AS VARCHAR)   AS plannedEndDate,
    CAST(completionDate AS VARCHAR)   AS completionDate,
    CASE WHEN status = 'overdue'
         THEN GREATEST(0, date_diff('day', plannedStartDate, CURRENT_DATE)) ELSE 0 END AS overdueDays,
    CASE WHEN actualStartDate IS NOT NULL
         THEN date_diff('day', actualStartDate, COALESCE(completionDate, CURRENT_DATE)) ELSE 0 END AS cycleDays,
    CASE status
      WHEN 'overdue' THEN 0 WHEN 'behindSchedule' THEN 1
      WHEN 'onSchedule' THEN 2 WHEN 'inProgress' THEN 2
      WHEN 'upcoming' THEN 3 WHEN 'notStarted' THEN 3
      ELSE 4
    END AS statusOrder
  FROM classified
  ORDER BY statusOrder, plannedStartDate, taskTitle
  `
}

/**
 * Pace card (PM lite): the schedule's actual % at each week boundary going back `weeks` weeks
 * plus the same window before it (for the "vs previous" verdict), and the tasks completed in
 * each of those weeks. One row per week index k (0 = the week ending today, 1 = the week
 * before, …); the extra row k = 2·weeks is the base for the oldest week's gain.
 *   progressPct   actual % on the curve at (today − 7k days), 0 before the curve starts
 *   completed     tasks whose completion date falls in week k
 */
export function paceSql(scope = {}) {
  const { weeks = 8 } = scope
  const n = Number(weeks) * 2
  return `
  WITH ${scopedTaskCtes(scope)},
  curve AS (${sCurveSql(scope)}),
  wk AS (SELECT CAST(range AS INTEGER) AS k FROM range(0, ${n + 1})),
  pct AS (
    SELECT w.k,
           (SELECT c.actualProgressPct FROM curve c
             WHERE c.date <= CURRENT_DATE - w.k * 7 AND c.actualProgressPct IS NOT NULL
             ORDER BY c.date DESC LIMIT 1) AS progressPct
    FROM wk w
  ),
  done AS (
    SELECT CAST(FLOOR(date_diff('day', lastDate, CURRENT_DATE) / 7) AS INTEGER) AS k,
           COUNT(*) AS completed
    FROM scoped_tasks
    WHERE completed AND lastDate IS NOT NULL
      AND date_diff('day', lastDate, CURRENT_DATE) BETWEEN 0 AND ${n * 7 - 1}
    GROUP BY 1
  )
  SELECT p.k, COALESCE(p.progressPct, 0) AS progressPct, COALESCE(d.completed, 0) AS completed
  FROM pct p
  LEFT JOIN done d ON d.k = p.k
  ORDER BY p.k
  `
}

// ── Task locations (ViewableLocations) ───────────────────────────────────────
// Each task carries the locations it is viewable on. Like the other nested fields it arrives
// as a JSON string whose element shape isn't pinned down, so it is parsed defensively: a plain
// string is taken as the location id, an object is read through the usual id keys (and a
// DrawingId when present). The drawing (and its title) is then resolved through the locations table.
//
//   task_locations        taskId · locationId · drawingId (NULL when only the location is known)
//   leaf_task_locations   one row per leaf task × location, taken from the leaf's own viewable
//                         locations or, failing that, the nearest ancestor's (distance = how many
//                         levels up). Resolved to the drawing and the location title. Leaf tasks
//                         with no location anywhere have no row here.
//                         planViewUrl opens the location in RDrive's plan viewer:
//                           {siteUrl}/{projectCode}/plan-viewer/{drawingId}/#view/location{locationId}
const EMPTY_TASK_LOCATIONS = `CREATE OR REPLACE TABLE task_locations (taskId VARCHAR, locationId VARCHAR, drawingId VARCHAR, raw VARCHAR)`
const EMPTY_LEAF_TASK_LOCATIONS = `CREATE OR REPLACE TABLE leaf_task_locations (taskId VARCHAR, distance INTEGER, locationId VARCHAR, locationTitle VARCHAR, drawingId VARCHAR, planViewUrl VARCHAR)`

const createLeafTaskLocationsTable = async (conn, { siteUrl = '', projectCode = '' } = {}) => {
  // Plan-viewer link prefix; NULL links when the site or project isn't known (e.g. in tests)
  const base = siteUrl && projectCode
    ? `'${String(siteUrl).replace(/\/+$/, '').replace(/'/g, "''")}/${String(projectCode).replace(/'/g, "''")}/plan-viewer/'`
    : null
  await conn.queryRaw(`
    CREATE OR REPLACE TABLE leaf_task_locations AS
    WITH located AS (
      SELECT a.taskId, a.distance, tl.locationId, tl.drawingId
      FROM task_ancestors a
      JOIN task_locations tl ON tl.taskId = a.ancestorId
      JOIN leaf_tasks lt ON lt.taskId = a.taskId
      WHERE tl.locationId IS NOT NULL OR tl.drawingId IS NOT NULL
    ),
    nearest AS (
      SELECT taskId, MIN(distance) AS distance FROM located GROUP BY taskId
    )
    SELECT DISTINCT
      lo.taskId,
      CAST(lo.distance AS INTEGER)           AS distance,
      lo.locationId,
      l.title                                AS locationTitle,
      COALESCE(lo.drawingId, l.drawingId)    AS drawingId,
      ${base
        ? `CASE WHEN COALESCE(lo.drawingId, l.drawingId) IS NOT NULL AND lo.locationId IS NOT NULL
                THEN ${base} || COALESCE(lo.drawingId, l.drawingId) || '/#view/location' || lo.locationId END`
        : 'CAST(NULL AS VARCHAR)'}            AS planViewUrl
    FROM located lo
    JOIN nearest n ON n.taskId = lo.taskId AND n.distance = lo.distance
    LEFT JOIN locations l ON l.id = lo.locationId
  `)
  const info = (await conn.queryRaw(`
    SELECT (SELECT COUNT(*) FROM leaf_tasks)                                            AS leafTasks,
           COUNT(DISTINCT taskId)                                                        AS located,
           COUNT(DISTINCT taskId) FILTER (WHERE distance = 0)                            AS own,
           COUNT(DISTINCT taskId) FILTER (WHERE drawingId IS NULL)                       AS noDrawing
    FROM leaf_task_locations
  `)).toArray()[0].toJSON()
  console.log(`[taskTables] leaf_task_locations: ${info.located} of ${info.leafTasks} leaf tasks have a location (${info.own} their own, the rest inherited); ${info.noDrawing} of them resolve to no drawing`)
}

export async function createTaskLocationsTable(conn, taskColumns, options = {}) {
  const column = taskColumns.find(c => /viewablelocation/i.test(c))
  if (!column) {
    console.warn('[taskTables] tasks has no ViewableLocations column; task_locations will be empty')
    await conn.queryRaw(EMPTY_TASK_LOCATIONS)
    await conn.queryRaw(EMPTY_LEAF_TASK_LOCATIONS)
    return
  }
  try {
    const type = (await conn.queryRaw(
      `SELECT data_type FROM information_schema.columns WHERE table_name = 'tasks' AND column_name = '${column}'`
    )).toArray()[0]?.toJSON().data_type ?? ''
    const value = /VARCHAR|JSON/i.test(type) ? `CAST(t.${column} AS VARCHAR)` : `CAST(to_json(t.${column}) AS VARCHAR)`
    await conn.queryRaw(`
      CREATE OR REPLACE TABLE task_locations AS
      WITH src AS (
        SELECT t.Id AS taskId, ${value} AS value
        FROM tasks t
        JOIN default_schedule ds ON ds.Id = t.ScheduleId
        WHERE t.${column} IS NOT NULL AND TRIM(${value}) NOT IN ('', '[]', 'null')
      ),
      split AS (
        SELECT taskId,
          CASE
            WHEN json_valid(value) AND json_type(value::JSON) = 'ARRAY'  THEN json_extract(value::JSON, '$[*]')
            WHEN json_valid(value) AND json_type(value::JSON) = 'OBJECT' THEN [value::JSON]
            ELSE list_transform(string_split_regex(value, '[,;\\s]+'), x -> to_json(x))
          END AS elements
        FROM src
      )
      SELECT
        s.taskId,
        NULLIF(TRIM(COALESCE(
          json_extract_string(u.e, '$.LocationId'),
          json_extract_string(u.e, '$.locationId'),
          json_extract_string(u.e, '$.Id'),
          json_extract_string(u.e, '$.id'),
          CASE WHEN json_type(u.e) = 'VARCHAR' THEN json_extract_string(u.e, '$') END
        )), '') AS locationId,
        NULLIF(TRIM(COALESCE(
          json_extract_string(u.e, '$.DrawingId'),
          json_extract_string(u.e, '$.drawingId')
        )), '') AS drawingId,
        CAST(u.e AS VARCHAR) AS raw
      FROM split s
      CROSS JOIN UNNEST(s.elements) AS u(e)
    `)
    const info = (await conn.queryRaw(`
      SELECT COUNT(*) AS links,
             COUNT(*) FILTER (WHERE drawingId IS NOT NULL
                               OR locationId IN (SELECT id FROM locations)) AS resolved,
             MIN(raw) AS sample
      FROM task_locations
    `)).toArray()[0].toJSON()
    console.log(`[taskTables] task_locations: ${info.links} links, ${info.resolved} resolve to a drawing (column ${column}, type ${type}); sample element: ${info.sample ?? '–'}`)
    await createLeafTaskLocationsTable(conn, options)
  } catch (error) {
    console.error('[taskTables] failed to parse task locations:', error.message)
    await conn.queryRaw(EMPTY_TASK_LOCATIONS)
    await conn.queryRaw(EMPTY_LEAF_TASK_LOCATIONS)
  }
}

/**
 * Progress by location card (PM lite): one row per leaf task × viewable location, with the
 * location's drawing and drawing set, so the card can roll the tasks up as a hierarchy
 * (drawing set → drawing → location, counting each task once per level). Locations come from
 * leaf_task_locations (the leaf's own, else the nearest ancestor's). Status uses the lite rules.
 *   locations: id · title · drawingId     drawings: id · title · drawingSetId · drawingSetTitle
 */
export function progressByLocationSql(scope = {}) {
  return `
  WITH ${scopedTaskCtes(scope)},
  -- one row per leaf × location from leaf_task_locations (own, else nearest ancestor's)
  task_location AS (
    SELECT DISTINCT taskId, locationId, locationTitle, drawingId
    FROM leaf_task_locations
    WHERE taskId IN (SELECT taskId FROM scoped_tasks) AND drawingId IS NOT NULL
  ),
  -- the drawing's own title and its drawing set, both columns of the drawings table.
  -- Every column is aliased: the parquet columns keep their own casing otherwise, and the
  -- card reads the row by these exact names.
  drawing_info AS (
    SELECT d.id AS drawingId, d.title AS drawingTitle,
           d.drawingSetId AS drawingSetId, d.drawingSetTitle AS drawingSetTitle
    FROM drawings d
  )
  SELECT
    tl.taskId,
    CASE WHEN s.completed THEN 'completed' WHEN s.actualPct > 0 THEN 'inProgress' ELSE 'notStarted' END AS status,
    di.drawingSetId                         AS drawingSetId,
    di.drawingSetTitle                      AS drawingSetTitle,
    tl.drawingId                            AS drawingId,
    COALESCE(di.drawingTitle, tl.drawingId) AS drawingTitle,
    tl.locationId                           AS locationId,
    tl.locationTitle                        AS locationTitle
  FROM task_location tl
  JOIN scoped_tasks s ON s.taskId = tl.taskId
  LEFT JOIN drawing_info di ON di.drawingId = tl.drawingId
  ORDER BY di.drawingSetTitle, drawingTitle, tl.locationTitle, tl.taskId
  `
}
